// API helpers with retry logic and error handling
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
export const API = `${BACKEND_URL}/api`;

// Create axios instance with timeout
export const api = axios.create({ 
  baseURL: API, 
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and retry
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.url} - Status: ${response.status}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log error
    console.error(`API Error: ${originalRequest.url}`, error.message);
    
    // Retry on network errors or 5xx errors (max 3 retries)
    if (
      !error.response && 
      !originalRequest._retry &&
      (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK')
    ) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      
      if (originalRequest._retryCount <= 3) {
        const delay = Math.pow(2, originalRequest._retryCount) * 1000; // Exponential backoff
        console.log(`Retrying request (${originalRequest._retryCount}/3) after ${delay}ms...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return api(originalRequest);
      }
    }
    
    // Handle 429 rate limit
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 5;
      console.log(`Rate limited. Retrying after ${retryAfter} seconds...`);
      
      await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
      return api(originalRequest);
    }
    
    // Handle 502/503/504 with retry
    if (
      error.response?.status >= 502 &&
      error.response?.status <= 504 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      console.log('Server error, retrying...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      return api(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

// API functions with error handling
export const fetchModels = async () => {
  try {
    const response = await api.get("/models");
    return response.data;
  } catch (error) {
    console.error('Failed to fetch models:', error);
    throw new Error('Unable to load available models. Please check your connection.');
  }
};

export const analyzeText = async (payload) => {
  try {
    const response = await api.post("/analyze", payload);
    return response.data;
  } catch (error) {
    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }
    if (error.response?.status === 502) {
      throw new Error('Analysis service temporarily unavailable. Please try again.');
    }
    console.error('Analysis failed:', error);
    throw new Error('Analysis failed. Please try again.');
  }
};

export const analyzeUpload = async (file, contentType, model) => {
  try {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("content_type", contentType);
    fd.append("model", model);
    
    const response = await api.post("/analyze/upload", fd, { 
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000 // 3 minutes for uploads
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 413) {
      throw new Error('File too large. Maximum size is 25MB.');
    }
    if (error.response?.status === 429) {
      throw new Error('Too many uploads. Please wait a moment and try again.');
    }
    console.error('Upload analysis failed:', error);
    throw new Error('Upload failed. Please try again.');
  }
};

export const fetchHistory = async () => {
  try {
    const response = await api.get("/analyses");
    return response.data;
  } catch (error) {
    console.error('Failed to fetch history:', error);
    throw new Error('Unable to load analysis history.');
  }
};

export const fetchAnalysis = async (id) => {
  try {
    const response = await api.get(`/analyses/${id}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Analysis not found.');
    }
    console.error('Failed to fetch analysis:', error);
    throw new Error('Unable to load analysis.');
  }
};

export const deleteAnalysis = async (id) => {
  try {
    const response = await api.delete(`/analyses/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete analysis:', error);
    throw new Error('Unable to delete analysis.');
  }
};

export const chat = async (message, language = "en", sessionId = null, factCheck = false) => {
  try {
    const response = await api.post("/chat", {
      message,
      language,
      session_id: sessionId,
      fact_check: factCheck,
    });
    return response.data;
  } catch (error) {
    console.error('Chat failed:', error);
    throw new Error('Chat service unavailable. Please try again.');
  }
};

export const textToSpeech = async (text, language = "en") => {
  try {
    const response = await api.post("/chat/tts", {
      text,
      language,
    });
    return response.data;
  } catch (error) {
    console.error('TTS failed:', error);
    throw new Error('Text-to-speech unavailable.');
  }
};

export const getHealth = async () => {
  try {
    const response = await api.get("/health");
    return response.data;
  } catch (error) {
    console.error('Health check failed:', error);
    return { status: 'unhealthy' };
  }
};
